import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CalendarHeart, ChevronDown,
  Users, Music2, Baby, HandHeart,
} from 'lucide-react';
import { Button } from '../components/ds/Button';
import { Reveal, Eyebrow, SectionTitle } from '../components/ds/theme';
import worshipBg from '../assets/images/worship_bg.jpg';
import prayer2 from '../assets/images/prayer2.jpg';
import sundaySchoolBg from '../assets/images/sunday_school_bg.jpg';

const EVENTS_FEED_URL = 'https://eventsfeed-jw6bkm54ga-ue.a.run.app';
const HERO_CONTENT_URL = 'https://us-east1-blbc-d5173.cloudfunctions.net/heroContent';

const DEFAULT_HERO = {
  headline: 'Welcome Home',
  subtitle: "Where God's love is shared and His plan is preached.",
  heroImageUrl: null,
  announcement: null,
};

// Fallback events shown until the feed responds (or if the church hasn't
// added any upcoming events yet).
const DEFAULT_EVENTS = [
  { id: 'd1', imageUrl: worshipBg, dateLabel: 'Every Sunday · 11 AM', title: 'Sunday Worship Service', description: 'Inspiring worship, biblical teaching, and fellowship with our church family. Everyone is welcome.', recurring: true },
  { id: 'd2', imageUrl: prayer2, dateLabel: 'Every Wednesday · 6:30 PM', title: 'Wednesday Bible Study', description: 'Gather midweek for Bible study, prayer, and spiritual encouragement.', recurring: true },
  { id: 'd3', imageUrl: sundaySchoolBg, dateLabel: 'June 15–19, 2026', title: 'Vacation Bible School', description: 'A week of fun, learning, and growing in faith for children ages 4–12. Theme: Heroes of Faith.', recurring: false },
];

function GhostCTA({ children, onClick, icon, to }) {
  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '9px',
    padding: '15px 28px',
    cursor: 'pointer',
    borderRadius: 'var(--radius-pill)',
    fontFamily: 'var(--font-ui)',
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
    background: 'rgba(255,255,255,0.08)',
    border: '1.5px solid rgba(255,255,255,0.55)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    transition: 'background 0.25s var(--ease), border-color 0.25s var(--ease)',
    textDecoration: 'none',
  };
  const onEnter = (e) => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
    e.currentTarget.style.borderColor = '#fff';
  };
  const onLeave = (e) => {
    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)';
  };
  const content = (<>{icon}{children}</>);
  if (to) {
    return <Link to={to} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>{content}</Link>;
  }
  return (
    <button onClick={onClick} style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {content}
    </button>
  );
}

function EventCard({ event }) {
  // Feed events have an id — link straight to the shareable detail page.
  // Fallback events (locally-defined defaults) don't, so send those to
  // the grid instead. No router-state hand-off — the detail page fetches
  // the event by id, and passing the decorated object here would blow
  // up history.pushState (Icon field is a non-cloneable forwardRef).
  const target = event.id ? `/events/${event.id}` : '/events';
  return (
    <Link to={target} style={{ display: 'block', textDecoration: 'none', color: 'inherit', height: '100%' }}>
      <article style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.35s var(--ease), box-shadow 0.35s var(--ease), border-color 0.35s var(--ease)',
      }}
      onMouseEnter={(ev) => {
        ev.currentTarget.style.transform = 'translateY(-6px)';
        ev.currentTarget.style.boxShadow = 'var(--shadow-soft)';
        ev.currentTarget.style.borderColor = 'var(--tan-400)';
      }}
      onMouseLeave={(ev) => {
        ev.currentTarget.style.transform = 'translateY(0)';
        ev.currentTarget.style.boxShadow = 'none';
        ev.currentTarget.style.borderColor = 'var(--line)';
      }}>
        <div style={{ height: '210px', background: `center/cover no-repeat url(${event.imageUrl || worshipBg})` }} />
        <div style={{ padding: '30px 30px 32px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-ui)', fontSize: '11.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'var(--tan-700)' }}>
              {event.dateLabel || event.date}
            </span>
            {event.recurring && (
              <span style={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--burgundy-700)', background: 'var(--burgundy-100)', padding: '3px 10px', borderRadius: 'var(--radius-pill)' }}>Recurring</span>
            )}
          </div>
          <h3 className="v2-serif" style={{ fontSize: '26px', color: 'var(--navy-800)', margin: '0 0 12px', lineHeight: 1.18, fontWeight: 500 }}>{event.title}</h3>
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: '15px', color: 'var(--text-body)', lineHeight: 1.7, margin: '0 0 20px' }}>
            {event.description}
          </p>
          <span style={{ marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, color: 'var(--burgundy-700)' }}>
            Learn more <ArrowRight size={16} />
          </span>
        </div>
      </article>
    </Link>
  );
}

function Home() {
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [events, setEvents] = useState(DEFAULT_EVENTS);

  useEffect(() => {
    fetch(HERO_CONTENT_URL)
      .then((r) => r.json())
      .then((data) => setHero({ ...DEFAULT_HERO, ...data }))
      .catch(() => {});
    fetch(EVENTS_FEED_URL)
      .then((r) => r.json())
      .then((data) => {
        const feed = (data.events || []).slice(0, 3);
        if (feed.length) setEvents(feed);
      })
      .catch(() => {});
  }, []);

  const ministries = [
    { Icon: Users, title: 'Youth Ministry', desc: 'Building faith and friendship in our next generation.' },
    { Icon: Music2, title: 'Worship Team', desc: 'Leading hearts in praise and worship each week.' },
    { Icon: Baby, title: "Children's Ministry", desc: 'Nurturing young hearts to know and love Jesus.' },
    { Icon: HandHeart, title: 'Outreach', desc: "Serving our community with Christ's love." },
  ];
  const services = [
    { label: 'Sunday Worship', day: 'Sunday', time: '11:00 AM' },
    { label: 'Sunday School', day: 'Sunday', time: '9:50 AM' },
    { label: 'Wednesday Bible Study', day: 'Wednesday', time: '6:30 PM' },
    { label: 'Where We Gather', day: 'Heath Springs', time: 'South Carolina' },
  ];

  const headlineRendered = (() => {
    // Split on the first " " so we can italicize the second word in the
    // common "Welcome Home" pattern. If headline is custom, render as-is
    // but preserve newlines.
    const text = hero.headline || 'Welcome Home';
    if (text === 'Welcome Home') {
      return (<>Welcome <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Home</em></>);
    }
    return <span style={{ whiteSpace: 'pre-line' }}>{text}</span>;
  })();

  return (
    <main>
      {hero.announcement && (
        <div style={{
          background: 'var(--burgundy-700)',
          color: '#fff',
          textAlign: 'center',
          fontSize: '14px',
          letterSpacing: '0.04em',
          padding: '10px 16px',
        }}>{hero.announcement}</div>
      )}

      {/* HERO */}
      <section style={{
        position: 'relative',
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: `center/cover no-repeat url(${hero.heroImageUrl || worshipBg}) var(--burgundy-900)`,
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--gradient-hero)', zIndex: 0, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--gutter)', width: '100%' }}>
          <div style={{ maxWidth: '760px' }}>
            <Reveal><Eyebrow onDark>Bright Light Baptist Church · Est. 1885</Eyebrow></Reveal>
            <Reveal delay={90}>
              <h1 className="v2-serif ds-hero-h1" style={{
                fontSize: 'clamp(50px, 7vw, 88px)',
                lineHeight: 0.98,
                color: '#fff',
                margin: '22px 0 0',
                fontWeight: 500,
                letterSpacing: '-0.02em',
                textShadow: '0 2px 30px rgba(10,5,6,0.4)',
              }}>{headlineRendered}</h1>
            </Reveal>
            <Reveal delay={170}>
              <p style={{
                fontFamily: 'var(--font-ui)',
                fontSize: '22px',
                color: 'rgba(255,255,255,0.94)',
                lineHeight: 1.55,
                margin: '26px 0 0',
                maxWidth: '540px',
                fontWeight: 400,
                textShadow: '0 1px 18px rgba(10,5,6,0.5)',
                whiteSpace: 'pre-line',
              }}>{hero.subtitle}</p>
            </Reveal>
            <Reveal delay={250}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '40px' }}>
                <Link to="/contact" style={{ textDecoration: 'none' }}>
                  <Button variant="primary" size="lg" iconLeft={<CalendarHeart size={19} />}>Plan Your Visit</Button>
                </Link>
                <GhostCTA to="/about" icon={<ArrowRight size={18} />}>Our Story</GhostCTA>
              </div>
            </Reveal>
          </div>
        </div>
        {/* scroll cue */}
        <div className="ds-hide-mobile" style={{ position: 'absolute', bottom: '30px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.22em' }}>
          <span>Scroll</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* SERVICE TIMES */}
      <section style={{ background: 'var(--white)', borderBottom: '1px solid var(--line)' }}>
        <div className="ds-grid-4" style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--gutter)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {services.map((s, i) => (
            <div key={s.label} style={{
              padding: '52px 36px',
              textAlign: 'center',
              borderLeft: i === 0 ? 'none' : '1px solid var(--line)',
            }}>
              <div style={{ fontFamily: 'var(--font-ui)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--tan-700)', marginBottom: '14px' }}>{s.label}</div>
              <div className="v2-serif" style={{ fontSize: '15px', color: 'var(--text-muted)', lineHeight: 1.3 }}>{s.day}</div>
              <div className="v2-serif" style={{ fontSize: '30px', color: 'var(--burgundy-700)', lineHeight: 1.15, marginTop: '2px' }}>{s.time}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WELCOME */}
      <section className="ds-section-y" style={{ background: 'var(--cream)', padding: 'var(--section-y) var(--gutter)' }}>
        <div className="ds-grid-2" style={{ maxWidth: '1180px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <Reveal>
            <SectionTitle align="left" eyebrow="Welcome" title="A church family that welcomes you" />
            <p style={{ fontFamily: 'var(--font-ui)', fontSize: '18px', lineHeight: 1.85, color: 'var(--text-body)', margin: '28px 0 0' }}>
              At Bright Light Baptist Church, we believe in the power of community, the strength of faith, and the transforming love of Jesus Christ. For decades, we've been a beacon of hope in Heath Springs.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '32px 0 0' }}>
              <span style={{ width: '40px', height: '1.5px', background: 'var(--tan-600)' }} />
              <span className="v2-serif" style={{ fontStyle: 'italic', fontSize: '19px', color: 'var(--navy-800)' }}>Pastor William S. Coleman &amp; Congregation</span>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-soft)', height: '460px' }}>
              <img src={prayer2} alt="Congregation in prayer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* EVENTS */}
      <section className="ds-section-y" style={{ background: 'var(--white)', padding: 'var(--section-y) var(--gutter)' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto' }}>
          <Reveal><SectionTitle eyebrow="This Season" title="Upcoming events" subtitle="Join us for worship, fellowship, and community throughout the year." /></Reveal>
          <div className="ds-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '34px', marginTop: '64px' }}>
            {events.map((e, i) => (
              <Reveal key={e.id || e.title} delay={i * 90}>
                <EventCard event={e} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MINISTRIES */}
      <section className="ds-section-y" style={{ background: 'var(--tan-100)', padding: 'var(--section-y) var(--gutter)' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto' }}>
          <Reveal><SectionTitle eyebrow="Get Involved" title="Our ministries" subtitle="Find your place to serve, grow, and belong." /></Reveal>
          <div className="ds-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '26px', marginTop: '64px' }}>
            {ministries.map((m, i) => (
              <Reveal key={m.title} delay={i * 80}>
                <Link to="/ministries" style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
                  <div style={{
                    background: 'var(--white)',
                    padding: '38px 28px 34px',
                    borderRadius: 'var(--radius-lg)',
                    height: '100%',
                    border: '1px solid transparent',
                    boxShadow: 'var(--shadow-md)',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '14px',
                    transition: 'transform 0.3s var(--ease), box-shadow 0.3s var(--ease), border-color 0.3s var(--ease)',
                  }}
                  onMouseEnter={(ev) => {
                    ev.currentTarget.style.transform = 'translateY(-6px)';
                    ev.currentTarget.style.boxShadow = 'var(--shadow-soft)';
                    ev.currentTarget.style.borderColor = 'var(--tan-400)';
                  }}
                  onMouseLeave={(ev) => {
                    ev.currentTarget.style.transform = 'translateY(0)';
                    ev.currentTarget.style.boxShadow = 'var(--shadow-md)';
                    ev.currentTarget.style.borderColor = 'transparent';
                  }}>
                    <div style={{
                      width: '58px',
                      height: '58px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      background: 'var(--gradient-brand)',
                      borderRadius: '50%',
                    }}><m.Icon size={27} /></div>
                    <h3 className="v2-serif" style={{ fontSize: '21px', color: 'var(--navy-800)', margin: 0, lineHeight: 1.2, fontWeight: 500 }}>{m.title}</h3>
                    <p style={{ fontFamily: 'var(--font-ui)', fontSize: '14.5px', color: 'var(--text-body)', lineHeight: 1.65, margin: 0 }}>{m.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
